import { gql } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import PopOverContainer from '../../components/deprecated/PopOverContainer';

import type {
  CommentThread_PostFragment,
  CommentThread_ThreadFragment,
} from '../../graphql/generated/graphqlTypes';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem from '../MagneticWallItem';
import CommentThreadCompact from './CommentThreadCompact';
import type { CommentThreadFullProps } from './CommentThreadFull';
import CommentThreadFull from './CommentThreadFull';
import CommentThreadMinimized from './CommentThreadMinimized';
import { isClickingMagneticWallItemOverflowButton } from './MagneticWallItemOverflowMenu';
import type { ResizeHandler } from '../MagneticWall';
import { EditorActions } from '../../editor/editorSlice';
import { useAppDispatch } from '../../app/redux/useAppSelector';

const POST_FRAGMENT = gql`
  fragment CommentThread_post on RootPostFields {
    ...CommentThreadFull_post
  }
  ${CommentThreadFull.fragments.post}
`;

const THREAD_FRAGMENT = gql`
  fragment CommentThread_thread on PublicThreadFields {
    id
    ...CommentThreadFull_thread
    ...CommentThreadCompact_thread
    ...CommentThreadMinimized_thread
  }
  ${CommentThreadFull.fragments.thread}
  ${CommentThreadCompact.fragments.thread}
  ${CommentThreadMinimized.fragments.thread}
`;

type CommentThreadProps = {
  thread: CommentThread_ThreadFragment;
  post: CommentThread_PostFragment;
  isFocused?: boolean;
  minimized?: boolean;
  isPopOverOpen?: boolean;
  initialShowingMore?: boolean;
  onResize: ResizeHandler;
  size: MagneticWallItemSize;
  widerLineMargin: number;
} & Pick<
  CommentThreadFullProps,
  'unsavedInputValue' | 'onInputChange' | 'unresolveThread'
>;

const CommentThread = ({
  thread,
  post,
  isFocused = false,
  minimized = false,
  isPopOverOpen = false,
  initialShowingMore = false,
  onResize,
  unsavedInputValue = [],
  onInputChange,
  unresolveThread,
  size,
  widerLineMargin,
}: CommentThreadProps) => {
  const dispatch = useAppDispatch();
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    onResize?.();
  }, [onResize]);

  useEffect(() => {
    if (isHovered) {
      dispatch(EditorActions.highlightComment(thread.id));
    } else {
      dispatch(EditorActions.removeMagneticWallHighlight());
    }
  }, [dispatch, thread.id, isHovered]);

  const handleClick = (e: React.MouseEvent) => {
    if (
      !isPopOverOpen &&
      !isClickingMagneticWallItemOverflowButton(e.nativeEvent)
    ) {
      dispatch(EditorActions.focusComment({ id: thread.id, isActive: true }));
    }
  };

  const handleBlur = () => {
    dispatch(EditorActions.blurMagneticWall());
  };

  const fullViewProps = {
    onResize,
    thread,
    unsavedInputValue,
    initialShowingMore,
    onInputChange,
    post,
  };

  return (
    <MagneticWallItem
      id={`thread-${thread.id}`}
      focused={isFocused}
      minimized={minimized}
      isPopOverOpen={isPopOverOpen}
      widerLineMargin={widerLineMargin}
      popOverContent={
        <PopOverContainer noPadding width="auto">
          {(popoverProps: { scrollToBottom: () => void }) => (
            <CommentThreadFull
              {...fullViewProps}
              isExpanded={isFocused}
              onCreated={() => {
                setTimeout(popoverProps.scrollToBottom, 200);
              }}
              unresolveThread={unresolveThread}
            />
          )}
        </PopOverContainer>
      }
      fullView={
        <CommentThreadFull
          {...fullViewProps}
          isExpanded={isFocused && !minimized}
          hovered={isHovered}
          unresolveThread={unresolveThread}
        />
      }
      onClick={handleClick}
      onBlur={handleBlur}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      minimizedView={<CommentThreadMinimized thread={thread} />}
      compactView={
        <CommentThreadCompact
          isFocused={isFocused && !minimized}
          thread={thread}
        />
      }
      size={size}
    />
  );
};

CommentThread.fragments = { post: POST_FRAGMENT, thread: THREAD_FRAGMENT };

export default CommentThread;
